import type { StandardObject } from 'src/utils/amplitude/types/amplitudeObjects'
import useStorage from 'src/sdk/hooks/useStorage'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { createPortal } from 'react-dom'
import { useEffect, useState } from 'react'
import classNames from 'classnames'
import { ModalAppDktLogoIcon } from 'src/components/Icons/ModalAppDktLogoIcon'

import type { ActionAppsProps, ButtonsAppsProps } from './PopUpAppsflyer'

type PopUpInstallAppProps = {
  standardObject: StandardObject | null
  handleSendEvent: (action: ActionAppsProps, button: ButtonsAppsProps) => void
  handleClickYes: (button: ButtonsAppsProps) => void
}

export const POPUP_APP_INSTALL_EVENT_ID = 'popup-app'
const PopUpInstallAppVersionB = ({
  handleClickYes,
  handleSendEvent,
  standardObject,
}: PopUpInstallAppProps) => {
  const { storageGetItem, storageSetItem } = useStorage()
  const [showModalApp, setShowModalApp] = useState(false)
  const [isMounted, setIsMounted] = useState(false)

  const hasClosedCouponModal =
    storageGetItem('closedCouponModal', 'session') === 'true'

  const handleCouponModalSession = () => {
    storageSetItem('closedCouponModal', 'true', 'session')
    setShowModalApp(false)

    const event = new CustomEvent(POPUP_APP_INSTALL_EVENT_ID, {
      detail: { visible: false },
    })

    document.dispatchEvent(event)
  }

  const handleOnClose = ({ modalButtonName }: { modalButtonName: string }) => {
    handleCouponModalSession()
    handleSendEvent('X', 'install-app')
    dispatchAmplitudeEvent({
      eventName: 'Click Button Modal',
      eventData: {
        ...standardObject,
        'modal nome':
          'Ganhe 10% OFF na 1ª compra no APP! Use o cupom BEMVINDO!',
        'modal botao nome': modalButtonName,
      },
    })
  }

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>

    const revealModal = () => {
      timeout = setTimeout(() => {
        if (hasClosedCouponModal) {
          return
        }

        setShowModalApp(true)

        const event = new CustomEvent(POPUP_APP_INSTALL_EVENT_ID, {
          detail: { visible: true },
        })

        document.dispatchEvent(event)

        dispatchAmplitudeEvent({
          eventName: 'Modal Viewed',
          eventData: {
            ...standardObject,
            'modal nome':
              'Ganhe 10% OFF na 1ª compra no APP! Use o cupom BEMVINDO!',
          },
        })
      }, 5000)
    }

    if (document.readyState === 'complete') {
      revealModal()
    } else {
      window.addEventListener('load', revealModal, { once: true })
    }

    return () => {
      clearTimeout(timeout)
      window.removeEventListener('load', revealModal)
    }
  }, [hasClosedCouponModal, standardObject])

  if (hasClosedCouponModal || !isMounted) {
    return null
  }

  const modalContent = (
    <>
      <div
        className={classNames(
          'z-[9998] inset-0 fixed w-screen h-screen transition-opacity duration-300 ease-out bg-restructure-background-neutral-12 overflow-hidden overscroll-contain',
          showModalApp
            ? 'opacity-75 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        )}
      />

      {/* Mounted off-screen (translate-y-full) from the start instead of
          conditionally rendered, and revealed via transform only: this keeps
          the modal from being painted for the first time 5s after load, which
          is what made it win Largest Contentful Paint on slow sessions. */}
      <div
        id="popup-install-app"
        className={classNames(
          'flex flex-col w-[calc(100%-6px)] modal-install-app fixed bottom-0 left-0 right-0 z-[9999] bg-white pt-[55px] pb-8 px-4 gap-6 shadow-lg rounded-t-md m-auto transition-transform duration-300 ease-out',
          showModalApp
            ? 'translate-y-0 pointer-events-auto'
            : 'translate-y-full pointer-events-none'
        )}
      >
        <div className="flex items-center justify-center absolute top-[-32px] left-[40vw]">
          <ModalAppDktLogoIcon />
        </div>
        <div className="flex flex-col gap-8 items-center px-6">
          <p className="w-full text-lg leading-[25.2px] text-restructure-primary font-medium text-center">
            <strong className="font-extrabold">Ganhe 10% OFF</strong> na 1ª
            compra no APP! Use o cupom
            <strong className="text-primaryBlue font-semibold">
              {' '}
              BEMVINDO
            </strong>
          </p>
        </div>

        <div className="flex-1 justify-end flex flex-col gap-3">
          <button
            className="w-full h-12 flex items-center justify-center bg-primaryBlue text-white font-semibold text-font-size-4 rounded-round"
            aria-label="Baixar app decathlon"
            onClick={() => {
              handleCouponModalSession()
              handleSendEvent('Sim', 'install-app')
              handleClickYes('install-app')
              setShowModalApp(false)
              dispatchAmplitudeEvent({
                eventName: 'Click Button Modal',
                eventData: {
                  ...standardObject,
                  'modal nome':
                    'Ganhe 10% OFF na 1ª compra no APP Decatlhon usando o cupom BEMVINDO',
                  'modal botao nome': 'Quero 10% de desconto',
                },
              })
            }}
          >
            Ir para o APP Decathlon
          </button>
          <button
            className="w-full h-12 flex items-center justify-center font-semibold text-font-size-4 text-restructure-action bg-restructure-icon-secondary-inverted rounded-round"
            aria-label="close modal"
            onClick={() => {
              handleOnClose({ modalButtonName: 'Nao quero' })
            }}
          >
            Não quero
          </button>
        </div>
        <a
          className="text-secondary font-inter font-medium text-font-size-2 underline text-center"
          onClick={() => {
            storageSetItem('closedCouponModal', 'true', 'session')
            setShowModalApp(false)
          }}
          href="/servicos/regras-de-promocoes?srsltid=AfmBOoqgMM-Y4vBvD1R7L9kviqX-Rz4yUDoC2H9_yF5vGq_Ss0dxeQyi"
        >
          Consulte as regras aqui
        </a>
      </div>
    </>
  )

  return createPortal(modalContent, document.body)
}

export default PopUpInstallAppVersionB
